import api from './api'

/**
 * Get notifications for the current user
 * @param {Object} options - Query options
 * @param {number} options.limit - Max notifications to fetch
 * @param {boolean} options.unreadOnly - Only fetch unread notifications
 * @returns {Promise<{notifications: Array, unreadCount: number}>}
 */
export async function getNotifications({ limit = 50, unreadOnly = false } = {}) {
  const params = new URLSearchParams()
  if (limit) params.append('limit', limit.toString())
  if (unreadOnly) params.append('unreadOnly', 'true')

  const response = await api.get(`/notifications?${params.toString()}`)
  return response.data
}

/**
 * Mark a notification as read
 * @param {string} notificationId - The notification ID
 * @returns {Promise<{notification: Object}>}
 */
export async function markNotificationRead(notificationId) {
  const response = await api.patch(`/notifications/${notificationId}`, { read: true })
  return response.data
}

/**
 * Mark all notifications as read
 * @returns {Promise<{message: string, updated: number}>}
 */
export async function markAllNotificationsRead() {
  const response = await api.post('/notifications/mark-all-read')
  return response.data
}

/**
 * Get user's assigned tasks (collaborative submissions with pending sections)
 * @returns {Promise<{tasks: Array, summary: Object}>}
 */
export async function getMyTasks() {
  const response = await api.get('/my-tasks')
  return response.data
}

/**
 * Get notification preferences for the current user
 * @returns {Promise<{notificationPreferences: Object, availableTypes: Array}>}
 */
export async function getNotificationPreferences() {
  const response = await api.get('/notifications/preferences')
  return response.data
}

/**
 * Update notification preferences for the current user
 * @param {Object} preferences - The preferences to update
 * @param {boolean} preferences.emailEnabled - Whether email notifications are enabled
 * @param {Array<string>} preferences.emailTypes - Array of notification types to receive via email
 * @returns {Promise<{notificationPreferences: Object, message: string}>}
 */
export async function updateNotificationPreferences(preferences) {
  const response = await api.put('/notifications/preferences', preferences)
  return response.data
}
